import {
  FluidAlertTranslationOptions,
  FluidIconConfig,
  FluidSeverity,
  FluidTheme,
} from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { FluidI18nStore } from '../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { themeVar } from '../../../utils/themes/theme.variables';

interface AlertBodyProps extends FluidFunctionalComponentProps {
  title?: string;
  message?: string;
  identifier?: string;
  store?: FluidI18nStore;
  fluidTranslate?: FluidAlertTranslationOptions;
}

interface AlertIconProps extends FluidFunctionalComponentProps {
  hostedIcon?: any;
  loading: boolean;
  theme: FluidTheme;
  severity: FluidSeverity;
  identifier?: string;
}

// ================================================================ //
// -- Public Functional Components

/**
 * Alert Body
 */
export const AlertBody: FunctionalComponent<AlertBodyProps> = (
  props: AlertBodyProps
) => {
  const { title, message, store, fluidTranslate, identifier } = props;
  const translatedTitle = store?.translate(fluidTranslate?.alertTitle) || title;
  const translatedMessage =
    store?.translate(fluidTranslate?.message) || message;

  return (
    <div class='alert-body' data-test-id={`${identifier}-alert-body`}>
      {translatedTitle && (
        <div class='alert-title'>
          <strong class='title-text' data-test-id={`${identifier}-alert-title`}>
            {translatedTitle}
          </strong>
        </div>
      )}
      <div
        class='alert-message'
        aria-live='polite'
        data-test-id={`${identifier}-alert-message-container`}>
        {translatedMessage ? (
          <div
            innerHTML={translatedMessage}
            data-test-id={`${identifier}-alert-message`}
          />
        ) : (
          <slot name='message' />
        )}
      </div>
    </div>
  );
};

/**
 * Alert Icon
 */
export const AlertIcon: FunctionalComponent<AlertIconProps> = (
  props: AlertIconProps
) => {
  const { hostedIcon, loading, theme, severity, identifier } = props;
  const iconConfig = themeVar<FluidIconConfig>(
    theme,
    `icons.alert.${loading ? 'loading' : severity}`
  );

  return (
    <div class='alert-icon' data-test-id={`${identifier}-alert-icon-container`}>
      {hostedIcon ? (
        <slot name='icon' />
      ) : (
        <fluid-icon
          key='alert-icon'
          data-test-id={`${identifier}-alert-icon`}
          config={{
            ...iconConfig,
            rotate: loading,
            theme,
            overrideGlobalTheme: true,
          }}
        />
      )}
    </div>
  );
};
